import React from 'react';
import { useForm } from 'react-hook-form';
import { useCreateAlertMutation, useUpdateAlertMutation } from '../../../app/services/alertService';
import { useGetInstitutionListsQuery, useGetScreenListsQuery } from '../../../app/services/selectListService';
import {
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  Switch,
  OutlinedInput,
  Chip,
  Box,
} from '@mui/material';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import CKRichEditor from '../../../components/form/CKRichEditor';
import { showErrorToast } from '../../../lib/toastHandler';
import { toastResponseHandler } from '../../../lib/toastMessageHandler';
import { useDialog } from '../../../contexts/dialog-context';

interface AlertFormProps {
  alert?: Alert.AlertDto | null;
  mode: 'create' | 'edit';
}

const AlertForm: React.FC<AlertFormProps> = ({ alert, mode }) => {
    const { closeDialog} =  useDialog();
  const [createAlert, { isLoading: isCreating }] = useCreateAlertMutation();
  const [updateAlert, { isLoading: isUpdating }] = useUpdateAlertMutation();
  const { data: institutionsData } = useGetInstitutionListsQuery();

  // TTS state for alerts
  const [isTtsEnabled, setIsTtsEnabled] = React.useState<boolean>(false);
  const [language, setLanguage] = React.useState<string>('en-US');

  const isLoading = isCreating || isUpdating;
  const institutions = institutionsData?.data || [];

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<Alert.AlertCreateDto>({
    defaultValues: {
      title: '',
      message: '',
      contentType: 'Html',
      alertType: 'Info',
      duration: 15
    },
  });

  const institutionId = watch('institutionId');
  const { data: screensData } = useGetScreenListsQuery({ institutionId: institutionId ? Number(institutionId) : undefined });
  const screens = screensData?.data || [];
  const [selectedScreenIds, setSelectedScreenIds] = React.useState<string[]>([]);
  const initialInstitutionRef = React.useRef<boolean>(false);


  // Watch the message field for the rich text editor
  const messageValue = watch('message');

  // Set form values when editing
  React.useEffect(() => {
    if (mode === 'edit' && alert) {
      setValue('title', alert.title);
      setValue('message', alert.message);
      setValue('institutionId', alert.institutionId);
      setValue('contentType', 'Html'); // Always set to Html
      setValue('alertType', alert.alertType);
      setValue('duration', alert.duration);
      // populate TTS if available on the alert
      setIsTtsEnabled(!!(alert as any).isTtsEnabled);
      setLanguage((alert as any).language || 'en-US');
      // Don't set selected screens here — wait until screens list is loaded so labels render correctly
    } else if (mode === 'create') {
      reset();
      setValue('contentType', 'Html'); // Default to Html for new alerts
      setValue('alertType', 'Info'); // Default to Info
      setValue('duration', 30); // Default duration
      // Clear selected screens when creating a new alert
      setSelectedScreenIds([]);
      // reset TTS defaults
      setIsTtsEnabled(false);
      setLanguage('en-US');
    }
    // mark that initial form setup is complete so subsequent institution changes are treated as user changes
    initialInstitutionRef.current = true;
  }, [mode, alert, setValue, reset]);

  // When screens list loads (or changes), if we're editing an alert, pre-select any screens that belong to that institution
  React.useEffect(() => {
    if (mode === 'edit' && alert && screens && screens.length > 0) {
      const normalized = ((alert as any).normalizedScreenIds as string | undefined)?.split(',').map(s => s.trim()).filter(Boolean) || [];
      const available = normalized.filter(id => screens.find((s: any) => s.id?.toString() === id));
      if (available.length > 0) {
        setSelectedScreenIds(available);
      }
    }
  }, [mode, alert, screens]);

  React.useEffect(() => {
    if (!initialInstitutionRef.current) return;
    if (mode === 'create') {
      setSelectedScreenIds([]);
      return;
    }
    if (mode === 'edit' && alert) {
      const alertInst = alert.institutionId?.toString();
      if ((institutionId ? institutionId.toString() : undefined) !== alertInst) {
        setSelectedScreenIds([]);
      }
    }
  }, [institutionId, mode, alert]);

  const onSubmit = async (data: Alert.AlertCreateDto) => {
    try {
      // Validate message field manually since it's not using register
      if (!messageValue || messageValue.trim() === '' || messageValue === '<p><br></p>') {
        showErrorToast("Validation Error: Message is required");
        return;
      }

      const formattedData = {
        ...data,
        message: messageValue, // Use the rich text value
        duration: Number(data.duration), // Ensure duration is a number
        institutionId: Number(data.institutionId), // Ensure institutionId is a number
        normalizedScreenIds: selectedScreenIds.join(','),
        // include TTS settings
        isTtsEnabled,
        language,
      };

      if (mode === 'create') {
        var response = await createAlert(formattedData).unwrap();
        toastResponseHandler({ response, fallbackMessage: "Alert created successfully!" });
        closeDialog();

      } else if (mode === 'edit' && alert) {
        var response = await updateAlert({
          id: alert.id,
          ...formattedData,
        }).unwrap();
        toastResponseHandler({ response, fallbackMessage: "Alert updated successfully!" });
        closeDialog();
      }
      reset();
    } catch (error: any) {
      toastResponseHandler({ response: error });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-4 grid grid-cols-2 gap-x-4 ">
        <div className="space-y-2">
          <Label htmlFor="institutionId">
            Institution <span className="text-red-500">*</span>
          </Label>
          <FormControl fullWidth size="small">
            <Select
              value={watch('institutionId')?.toString() || ''}
              onChange={(e) => setValue('institutionId', parseInt(e.target.value))}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return <span style={{ color: '#9ca3af' }}>Select an institution</span>;
                }
                const selectedInstitution = institutions.find(i => i.id.toString() === selected);
                return selectedInstitution?.name;
              }}
              sx={{
                height: '36px',
                fontSize: '14px',
                borderRadius: '6px',
                backgroundColor: 'transparent',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: errors.institutionId ? '#ef4444' : '#e5e7eb',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: errors.institutionId ? '#ef4444' : '#e5e7eb',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#403399',
                  borderWidth: '2px',
                },
                '& .MuiSelect-select': {
                  padding: '8px 12px',
                }
              }}
            >
              {institutions.map((institution) => (
                <MenuItem key={institution.id} value={institution.id}>
                  {institution.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {errors.institutionId && (
            <p className="text-sm text-red-500">Institution is required</p>
          )}
        </div>

        {/* Title - Full width */}
        <div className="space-y-2">
          <Label htmlFor="title">
            Title <span className="text-red-500">*</span>
          </Label>
          <Input
            id="title"
            {...register('title', {
              required: { value: true, message: 'Title is required' },
            })}
            className={errors.title ? 'border-red-500' : ''}
          />
          {errors.title && (
            <p className="text-sm text-red-500">{errors.title.message}</p>
          )}
        </div>

        {/* Alert Type and Duration */}
        <div className="space-y-2">
          <Label htmlFor="alertType">
            Alert Type <span className="text-red-500">*</span>
          </Label>
          <FormControl fullWidth size="small">
            <Select
              value={watch('alertType') || ''}
              onChange={(e) => setValue('alertType', e.target.value as Alert.AlertType)}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return <span style={{ color: '#9ca3af' }}>Select alert type</span>;
                }
                return selected;
              }}
              sx={{
                height: '36px',
                fontSize: '14px',
                borderRadius: '6px',
                backgroundColor: 'transparent',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: errors.alertType ? '#ef4444' : '#e5e7eb',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: errors.alertType ? '#ef4444' : '#e5e7eb',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#403399',
                  borderWidth: '2px',
                },
                '& .MuiSelect-select': {
                  padding: '8px 12px',
                }
              }}
            >
              <MenuItem value="Info">Info</MenuItem>
              <MenuItem value="Warning">Warning</MenuItem>
              <MenuItem value="Emergency">Emergency</MenuItem>
              <MenuItem value="Flash News">Flash News</MenuItem>
            </Select>
          </FormControl>
          {errors.alertType && (
            <p className="text-sm text-red-500">Alert type is required</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="duration">
            Duration (seconds) <span className="text-red-500">*</span>
          </Label>
          <Input
            id="duration"
            type="number"
            {...register('duration', {
              required: { value: true, message: 'Duration is required' },
              min: { value: 1, message: 'Duration must be at least 1 second' },
              max: { value: 3600, message: 'Duration cannot exceed 3600 seconds (1 hour)' },
            })}
            className={errors.duration ? 'border-red-500' : ''}
            min={1}
            max={3600}
          />
          {errors.duration && (
            <p className="text-sm text-red-500">{errors.duration.message}</p>
          )}
        </div>

        {/* Screens multi-select (moved below Duration, no checkboxes) */}
        <div className="space-y-2 col-span-2">
          <Label>Screens</Label>
          <FormControl fullWidth size="small">
            <Select
              multiple
              displayEmpty
              value={selectedScreenIds}
              onChange={(e) => {
                const value = e.target.value;
                const next = typeof value === 'string' ? value.split(',') : (value as string[]);
                setSelectedScreenIds(next);
              }}
              input={<OutlinedInput />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((id) => {
                    const s = screens.find((sc: any) => sc.id?.toString() === id);
                    return <Chip key={id} label={s?.name || id} size="small" />;
                  })}
                </Box>
              )}
              sx={{
                height: '36px',
                fontSize: '14px',
                borderRadius: '6px',
                backgroundColor: 'transparent',
                '& .MuiSelect-select': { padding: '8px 12px' }
              }}
            >
              {screens.map((s: any) => (
                <MenuItem key={s.id} value={s.id.toString()}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {/* TTS options (above content) */}
        <div className="col">
          <Label id="alert-tts-language">Language</Label>
          <FormControl className='w-full' size="small" sx={{ minWidth: 160 }}>
            <Select
              labelId="alert-tts-language"
              value={language}
              variant='outlined'
              onChange={(e) => setLanguage(e.target.value as string)}
            >
              <MenuItem value="en-US">en-US</MenuItem>
              <MenuItem value="hi-IN">hi-IN</MenuItem>
            </Select>
          </FormControl>
        </div>
        <div className="flex mt-4 items-center gap-4">
          <Label id="alert-tts-language">Enable TTS</Label>
          <FormControlLabel
            control={<Switch checked={isTtsEnabled} onChange={(e) => setIsTtsEnabled(e.target.checked)} />}
            label=""
          />
        </div>

        {/* Message - Full width */}
        <div className="space-y-2 col-span-2">
          <Label htmlFor="message">
            Message
          </Label>
          <div className="min-h-[200px]">
            <CKRichEditor
              value={messageValue}
              onChange={(value: string) => setValue('message', value)}
              placeholder="Enter your alert message here..."
              height="200px"
            />
          </div>
          {errors.message && (
            <p className="text-sm text-red-500 mt-1">{errors.message.message}</p>
          )}
        </div>


      </div>
      <div className="flex mt-6 gap-2">
        <Button
          size="lg"
          type="button"
          variant="outline"
          className='flex-1'
          onClick={closeDialog}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          size="lg"
          type="button"
          onClick={handleSubmit(onSubmit)}
          disabled={isLoading}
          className='flex-1'
        >
          {isLoading
            ? (mode === 'create' ? 'Creating...' : 'Updating...')
            : (mode === 'create' ? 'Create Alert' : 'Update Alert')
          }
        </Button>
      </div>
    </form>
  );
};

export default AlertForm;